import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { AddableFieldData, DisableWhen, EnableWhen, ICodeAndLinkIdForObservations, IDisplayData } from 'src/interfaces/utility-interfaces';
import { HistoryOfCABGSurgeryEnum, PresenceOfCoronaryStenosisEnum } from 'src/constants/wle.enum';

import { AppConstants } from 'src/constants/constants';
import { BaseInput } from '../shared/models/base-input';
import { ClinicalConstants } from 'src/constants/clinical-constants';
import { DateTimeInput } from '../shared/models/date-time-input';
import { DropdownInput } from '../shared/models/dropdown-input';
import { Injectable } from '@angular/core';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class InputControlService {

  constructor(
    private utilService: UtilService
  ) { }

  checkEnabledWhen(form: FormGroup, formData: BaseInput<any>[], noreset?: boolean, previousValue?: string) {
    for (const field of formData) {
      if (field.enableWhen && field.enableWhen.length > 0) {
        let match = false;
        for (const item of field.enableWhen) {
          // revert previous 3320 PR 375 fix
          match = this.checkOneEnableWhenItem(form, field, item, previousValue);
          if (field.enableBehavior === 'all') {
            if (!match) {
              break;
            }
          } else if (match) {
            break; // 'any' behavior
          }
        }
        if (match && !field.show) {
          field.show = true;
          this.setValidator(field, form);
        } else if (!match && field.show) {
          if (!noreset) {
            form.get(field.linkId).reset();
            // For HCP or Wait Location, when any option is selected, the options length would be 1
            // So that engine would patch it as default value
            if (field.linkId.startsWith(AppConstants.OffListingProcedureHCPLinkId) ||
                field.linkId.startsWith(AppConstants.OffListingProcedureSecondHCPLinkId) ||
                field.linkId === AppConstants.nonPercutaneousHospitalLinkId) {
              (field as DropdownInput).options = [];
            }
            field.invalid = false;
            field.warning = false;
          }
          field.show = false;
          this.setValidator(field, form);
        }
      }
    }
    return formData;
  }

  addFhirResourceIds(formData: BaseInput<any>[], formName: string, entryDisplayData: IDisplayData[]) {
    if (formName) {
      switch (formName) {
        case 'service':
          //
          break;
        case 'referral':
          this.setFhirIdsForIndividualObservationItem(entryDisplayData, formData,
            ClinicalConstants.referralObservationsList, formName);
          break;
        case 'diagnostics':
          this.setFhirIdsForIndividualObservationItem(entryDisplayData, formData,
            ClinicalConstants.diagnosticsObservationList, formName);
          break;
        case 'acceptance':
          this.setFhirIdsForIndividualObservationItem(entryDisplayData, formData,
            ClinicalConstants.acceptanceObservationsListForServiceRequest, formName);
          break;
        case 'offlisting':
          this.setFhirIdsForIndividualObservationItem(entryDisplayData, formData,
            ClinicalConstants.offlistingObservationsListForServiceRequest
            .concat(ClinicalConstants.procedureObservationList)
            .concat(ClinicalConstants.stentElements), formName);
          break;
        case 'waitTime':
          this.setFhirIdsForIndividualObservationItem(entryDisplayData, formData,
            ClinicalConstants.waitTimesObservationsList, formName);
          break;
      }
    }
  }

  setFhirIdsForIndividualObservationItem(
    entryDisplayData: IDisplayData[],
    formData: BaseInput<any>[],
    observationsListForSection: ICodeAndLinkIdForObservations[],
    formName: string) {
    for (const item of entryDisplayData) {
      if (!item?.fhirResourceId) {
        continue;
      }
      const formFieldData = formData.find(data => data.linkId.startsWith(item.linkId));

      if (formName === 'acceptance' && item.linkId.includes(AppConstants.bookingDateFieldLinkId)
      && item.linkId === formFieldData.linkId) {
        // Set booking date fhir resource id
        formFieldData.fhirResourceId = item.fhirResourceId;
      }
      if (observationsListForSection.find(observationItem => item.linkId.startsWith(observationItem.linkId))) {
        formFieldData.fhirResourceId = item.fhirResourceId;
      }
    }
  }

  checkOneEnableWhenItem(form: FormGroup, field: BaseInput<any>, item: EnableWhen, previousValue?: string): boolean {
    let match = false;
    switch (item.operator) {
      case '=':
        if (form.get(item.question).value) {
          if (item.answer === form.get(item.question).value) {
            match = this.checkForVariations(form, item, field.linkId, previousValue);
          }
        } else {
          if (!item.answer) {
            match = true;
          }
        }
        break;
      case '!=':
        if (form.get(item.question).value) {
          if (item.answer !== form.get(item.question).value) {
            match = true;
          }
        }
        break;
      case 'exists':
        if (!field.readonly) {
          if ((field.linkId === AppConstants.allProceduresLinkId)) {
            // If the form is Offlisting, check for all procedures
            match = this.enableAllProcedures(form);
          } else if ((form.get(item.question).disabled || form.get(item.question).valid) && form.get(item.question)?.value) {
            match = true;
          }
        }
        break;
      default:
        match = true;
        break;
    }
    return match;
  }

  private enableAllProcedures(form: FormGroup) {
    let match = true;
    if (
      form.get(AppConstants.procedureServiceDetail1LinkId)?.value ||
      form.get(AppConstants.procedureServiceDetail2LinkId)?.value ||
      form.get(AppConstants.procedureServiceDetail3LinkId)?.value ||
      form.get(AppConstants.procedureServiceDetail4LinkId)?.value) {
      match = false;
    }
    return match;
  }

  checkForVariations(form: FormGroup, enableWhenItem: EnableWhen, fieldLinkId: string, previousValue?: string) {
    let result = true;
    if (enableWhenItem.question === AppConstants.diagnosticsPrimaryReferralType) {
      const serviceDetail2 = form.get(AppConstants.diagnosticsServiceDetail2)?.value;
      const primaryReasonType = form.get(AppConstants.diagnosticsPrimaryReferralType)?.value;
      if (primaryReasonType) {
        if (primaryReasonType === AppConstants.codeForSTEMI &&
          serviceDetail2 === AppConstants.serviceDetail2ForDiagnosticVariations) {
          return this.showHidePatientPresentationSection(form, fieldLinkId);
        } else {
          result = false;
        }
      }
    } else if (enableWhenItem.question.startsWith(AppConstants.allProceduresLinkId)) {
      let lastAdded = '';
      const parsedArray = enableWhenItem.question.split('-');
      if (!isNaN(+parsedArray[parsedArray.length - 1])) {
        lastAdded = '-' + parsedArray[parsedArray.length - 1];
      }
      // check procedure variations for offlisting section
      const procedureStatus = form.get(AppConstants.procedureStatusLinkId + lastAdded)?.value;
      if (procedureStatus !== AppConstants.proceduresCompleted) {
        result = false;
      }
      // #2208
      if (result && fieldLinkId.startsWith(AppConstants.stenosisLinkIdsPrefix)) {
        result = this.showHideOfflistingStenosisSection(form, fieldLinkId, lastAdded);
      }
    } else if (enableWhenItem.question === AppConstants.diagnosticsServiceDetail2) {
      // #2254, #2255, #2256
      result = this.showHideDiagnosticStenosisSection(form, fieldLinkId);
    } else if (enableWhenItem.question === AppConstants.removalReasonFieldLinkId) {
      // check procedure infomation subsection variation in offlisting section
      const removalReason = form.get(AppConstants.removalReasonFieldLinkId)?.value;
      if ((removalReason === AppConstants.removalReasonProcedureStarted) ||
        (removalReason === AppConstants.removalReasonDataError
          && previousValue && previousValue === AppConstants.removalReasonProcedureStarted)) {
        // result is default success
      } else {
        result = false;
      }
    } else if (fieldLinkId === AppConstants.deviceIndicationLinkId) {
      // #2583
      const serviceDetail2 = form.get(AppConstants.diagnosticsServiceDetail2)?.value;
      if (serviceDetail2 === AppConstants.serviceDetail2ForCtrp) {
        return false;
      }
      //#4667
      if(!serviceDetail2) { // if service detail 2 is empty don't show this field
        return false;
      }
    }
    return result;
  }

  private showHideDiagnosticStenosisSection(form: FormGroup, fieldLinkId: string): boolean {
    const historyStenosis = form.get(AppConstants.historyofCoronaryArteryBypassLinkId)?.value;
    if (!(historyStenosis === HistoryOfCABGSurgeryEnum.Yes || historyStenosis === HistoryOfCABGSurgeryEnum.Unknown)) {
      if (AppConstants.stenosisGraftIds.find(x => fieldLinkId.startsWith(x))) {
        return false;
      }
    }
    return true;
  }

  private showHideOfflistingStenosisSection(form: FormGroup, fieldLinkId: string, lastAdded: string): boolean {
    const presence = form.get(AppConstants.presenceOfStenosisLinkId + lastAdded)?.value;
    if (presence !== PresenceOfCoronaryStenosisEnum.SignificantStenosis) {
      return false;
    }
    const historyStenosis = form.get(AppConstants.offlistingHistoryofCoronaryArteryBypass)?.value;
    if (!(historyStenosis === HistoryOfCABGSurgeryEnum.Yes || historyStenosis === HistoryOfCABGSurgeryEnum.Unknown)) {
      if (AppConstants.stenosisGraftIds.find(x => fieldLinkId.startsWith(x))) {
        return false;
      }
    }
    return true;
  }

  private showHidePatientPresentationSection(form: FormGroup, fieldLinkId: string): boolean {
    let result = true;
    const presentValue = form.get(AppConstants.patientPresentationLinkId)?.value;
    switch (fieldLinkId) {
      case AppConstants.nonPercutaneousHospitalLinkId:
        // Patient presentation is empty or not via ambulance/self to non-PCI hospital, hide non-PCI hospital
        if (!presentValue || !AppConstants.condition1.includes(presentValue)) {
          result = false;
        }
        break;
      case AppConstants.firstContactParamedicsLinkId:
      case AppConstants.ambulanceEcgLinkId:
        // Patient presentation is empty or not one of three "Ambulance" presentations
        if (!presentValue || !AppConstants.condition2.includes(presentValue)) {
          result = false;
        }
        break;
      case AppConstants.registrationDateLinkId:
      case AppConstants.firstHospitalEcgLinkId:
        // Patient presentation is empty or "in hospital patient"
        if (!presentValue || AppConstants.condition3Not.includes(presentValue)) {
          result = false;
        }
        break;
      case AppConstants.fibrinolysisGivenLinkId:
        // Fibrinolysis Administered not checked, hide this field
        if (form.get(AppConstants.fibrinolysisAdministeredLinkId).value !== 'Y') {
          result = false;
        }
        break;
      default:
        break;
    }
    return result;
  }

  checkDisabledWhen(form: FormGroup, formData: BaseInput<any>[]) {
    for (const field of formData) {
      if (field.disableWhen) {
        let match = false;
        for (const item of field.disableWhen) {
          // disables field if question field is filled
          if (item.operator === 'filled') {
            if (form.get(item.question).value && (form.get(item.question).valid ||
              form.get(item.question).disabled) && (!item.value ||
                item.value.includes(form.get(item.question).value))) {
              if (field.linkId[0] === AppConstants.lesionTypeLinkId[0] && field.linkId.includes(AppConstants.lesionTypeLinkId)) {
                form.get(field.linkId).reset();
              }
              match = true;
            }
            // disables field if question field is not filled
          } else if (item.operator === 'blank') {
            // #3320, for other fields has blank setting, such as service detail 1/2
            // the value must be a valid value to drive child show/hide
            // for the DART date though, must use use selected/input, no matter it's has cross field validation error or not
            let isEmpty = !form.get(item.question).value || form.get(item.question).invalid;
            if (field.linkId.startsWith (AppConstants.cancellationsAndBookingsCancellationReasonLinkId)) {
              const indexOfBookingDate = Number(field.linkId.split('-').slice(-1));
              let bookingDateLinkId = AppConstants.bookingDateFieldLinkId;
              if (indexOfBookingDate) {
                bookingDateLinkId = bookingDateLinkId + '-' + indexOfBookingDate.toString();
              }
              isEmpty = !form.getRawValue()[bookingDateLinkId];
            }
            if (isEmpty || this.checkAnotherInstance(field.linkId, formData)) {
              if (field.linkId === AppConstants.secondaryReasonReferralLinkId) {
                form.get(field.linkId).reset();
              }
              match = true;
            }
            // disables field if question field is filled and field is not filled
          } else if (item.operator === 'filled-blank') {
            if (form.get(item.question).value && !form.get(field.linkId).value) {
              match = true;
            }
            // disables field if multiple question fields are filled
          } else if (item.operator === 'filled-multi') {
            if (form.get(field.linkId).value === item.selfValue) {
              Object.keys(form.value).forEach(fieldLinkId => {
                if (form.value[fieldLinkId] && fieldLinkId.includes(item.question) &&
                  item.value.includes(form.value[fieldLinkId])) {
                  match = true;
                }
              });
            }
          }
        }
        if (match) {
          form.get(field.linkId).disable();
          field.disabled = true;
        } else if(form.get(field.linkId).disabled){
          form.get(field.linkId).enable();
          field.disabled = false;
        }
        // #4167
        if (field.linkId == AppConstants.cancellationsAndBookingsCancellationReasonLinkId + "-10") {
          form.get(field.linkId).disable();
        }
        // Only apply following enable/disable logic to user-preference
        if (form.get(AppConstants.defaultHomePageLinkId)) {
          this.setServiceLocationChildEnableDisable(form);
        }
      }
    }
    return formData;
  }

  setServiceLocationChildEnableDisable(form: FormGroup) {
    // If setup service category drive by service location,
    // then UI somehow render service area in same line as service category
    // If service location has value, enable service category
    if (form.get(AppConstants.serviceLocationLinkId).value) {
      form.get(AppConstants.entryCategoryLinkId).enable();
    } else {
      form.get(AppConstants.entryCategoryLinkId).disable();
      form.get(AppConstants.serviceAreaLinkId).disable();
    }


  }

  checkAnotherInstance(fieldId: string, formData: BaseInput<any>[]) {
    const lastAdded = +fieldId.slice(fieldId.lastIndexOf('-') + 1);
    return formData.find((field) => {
      if (isNaN(lastAdded)) {
        return field.linkId !== fieldId && field.linkId.includes(fieldId);
      } else {
        const lastChecked = +field.linkId.slice(field.linkId.lastIndexOf('-') + 1);
        return field.linkId.includes(fieldId.slice(0, -2)) && !isNaN(lastChecked) && lastChecked > lastAdded;
      }
    });
  }

  setValidator(field: BaseInput<any>, form: FormGroup) {
    (field.required && field.show) ? form.controls[field.linkId].setValidators(this.getValidators(field)) :
      form.controls[field.linkId].clearValidators();
    form.controls[field.linkId].updateValueAndValidity();
  }

  toFormGroup(questions: BaseInput<any>[]): FormGroup {
    const group: any = {};
    questions.forEach(question => {
      group[question.linkId] = (question.required && question.show) ? new FormControl(
        { value: question.value, disabled: question.disabled },
        this.getValidators(question)) : new FormControl({ value: question.value, disabled: question.disabled });
    });
    return new FormGroup(group);
  }

  getValidators(field: BaseInput<any>): ValidatorFn[] {
    const valArray: ValidatorFn[] = [Validators.required];
    if (field.validation) {
      const custValArray = this.getCustomValidators(field);
      if (custValArray.length > 0) {
        valArray.push(...custValArray);
      }
    }
    return valArray;
  }

  updateFormControls(form: FormGroup, fieldArray: BaseInput<any>[], type: string) {
    for (const field of fieldArray) {
      if (type === 'add') {
        form.addControl(field.linkId, (field.required && field.show) ? new FormControl(
          { value: field.value, disabled: field.disabled },
          this.getValidators(field)) : new FormControl({ value: field.value, disabled: field.disabled }));
      } else if (type === 'remove') {
        form.removeControl(field.linkId);
      }
    }
  }

  getCustomValidators(field: BaseInput<any>): ValidatorFn[] {
    const validationArray: ValidatorFn[] = [];
    switch (field.validation.type) {
      case 'number':
        if (field.validation.min) {
          validationArray.push(Validators.min(+field.validation.min));
        }
        if (field.validation.max) {
          validationArray.push(Validators.max(+field.validation.max));
        }
        if (field.validation.reg) {
          validationArray.push(Validators.pattern(field.validation.reg));
        }
        break;
      case 'date':
        if (field.validation.min) {
          validationArray.push(customDateValidator(field.validation.min, 'min'));
        }
        if (field.validation.max) {
          validationArray.push(customDateValidator(field.validation.max, 'max'));
        }
        if (field.validation.warning) {
          if (field.validation.warning.withinMin) {
            validationArray.push(warningValidator('date', field.validation.warning.withinMin, 'withinMin'));
          }
        }
        break;
      default:
        break;
    }
    return validationArray;
  }

  addNewAddableField(addableField: BaseInput<any>[], lastAdded: number, checkSuffix?: string): BaseInput<any>[] {
    const returnList: BaseInput<any>[] = [];

    for (const item of addableField) {
      const itemToAdd: BaseInput<any> = { ...item };
      if (itemToAdd.fhirResourceId) {
        delete itemToAdd.fhirResourceId;
      }
      if (AppConstants.dynamicPractitionerFields.find(practitioner => itemToAdd.linkId.includes(practitioner))) {
        const dropdownInput = itemToAdd as DropdownInput;
        dropdownInput.options = [];
      }
      itemToAdd.linkId = item.linkId + '-' + lastAdded;
      itemToAdd.errorMsg = '';
      itemToAdd.invalid = false;
      if (itemToAdd.enableWhen.length > 0) {
        const itemToAddEnableWhenList: EnableWhen[] = [];
        for (const enableWhen of itemToAdd.enableWhen) {
          const itemToAddEnableWhen: EnableWhen = { ...enableWhen };
          if (this.utilService.checkFieldIsAddable(enableWhen.question, addableField.map((field: BaseInput<any>) => field.linkId))) {
              // Fixed the issue of enablewhen, when it was adding extra 0 on adding procedure and stents
              // linkid and enablewhen question indexes should be same
              if( itemToAdd.linkId.includes('0') ){
                itemToAddEnableWhen.question = itemToAddEnableWhen.question + '-' + lastAdded;
              } 
              else {
                itemToAddEnableWhen.question = itemToAddEnableWhen.question.replace('-0', '') + '-' + lastAdded;
              } 
          }
          itemToAddEnableWhenList.push(itemToAddEnableWhen);
        }
        itemToAdd.enableWhen = itemToAddEnableWhenList;
      }

      if (itemToAdd.disableWhen) {
        const newDisableWhenList: DisableWhen[] = [];
        for (const disableWhenitem of itemToAdd.disableWhen) {
          const itemToAddDisableWhen: DisableWhen = { ...disableWhenitem };
          if (disableWhenitem.operator !== 'filled-multi') {
            if (!checkSuffix) {
              itemToAddDisableWhen.question = itemToAddDisableWhen.question.replace('-0', '');
            }
            itemToAddDisableWhen.question = itemToAddDisableWhen.question + '-' + lastAdded;
          }
          newDisableWhenList.push(itemToAddDisableWhen);
          if (disableWhenitem.operator === 'blank') {
            itemToAdd.disabled = true;
          }
        }
        itemToAdd.disableWhen = newDisableWhenList;
      }

      if (itemToAdd.controlType === 'label') {
        itemToAdd.label = '';
      }
      returnList.push(itemToAdd);
    }
    return returnList;
  }

  addAddableField(
    formData: BaseInput<any>[], form: FormGroup, subSectionIndex: number, subSection?: string[],
    excludeSubSection?: boolean, checkSuffix?: string): AddableFieldData {
    let formDataWithAddableField: BaseInput<any>[] = [];
    const addableField: BaseInput<any>[] = [];
    let addableFieldArray: BaseInput<any>[];
    let lastAdded: number;
    let addableFieldIdArray: string[];

    if (subSection) {
      for (const item of formData) {
        let match = false;
        for (const check of subSection) {
          if (item.linkId[0] === check[0] && item.linkId.includes(check) &&
            (checkSuffix ? this.utilService.checkSuffix(item.linkId, checkSuffix) : true)) {
            match = true;
          }
        }
        if (match !== !!excludeSubSection) {
          const parsedArray = item.linkId.split('-');
          if (!isNaN(+parsedArray[parsedArray.length - 1])) {
            lastAdded = +parsedArray[parsedArray.length - 1] + 1;
            if (checkSuffix) {
              if (isNaN(+parsedArray[parsedArray.length - 2])) {
                lastAdded = 1;
                addableField.push(item);
              }
            } else if (lastAdded === 1) {
              const subSectionItem = { ...item };
              subSectionItem.linkId = subSectionItem.linkId.replace('-0', '');
              addableField.push(subSectionItem);
            }
          } else {
            lastAdded = 1;
            addableField.push(item);
          }
        } else {
          if (lastAdded) {
            addableFieldArray = this.addNewAddableField(addableField, lastAdded, checkSuffix);
            formDataWithAddableField = formDataWithAddableField.concat(addableFieldArray);
            lastAdded = null;
          }
        }
        formDataWithAddableField.push(item);
      }
      if (lastAdded) {
        if (subSectionIndex === 0 && formDataWithAddableField.filter(x => x.linkId.startsWith('stent-')).length > 0) {
          addableFieldArray = formDataWithAddableField;
        } else {
          addableFieldArray = this.addNewAddableField(addableField, lastAdded);
          formDataWithAddableField = formDataWithAddableField.concat(addableFieldArray);
        }
      }
      formData = formDataWithAddableField;
      this.updateFormControls(form, addableFieldArray, 'add');
      formData = this.checkDisabledWhen(form, formData);
      addableFieldIdArray = addableFieldArray.map(field => field.linkId);
    }
    return {
      data: formData,
      fields: addableFieldIdArray
    };
  }

  checkDateTimeAllowUnknown(diagnosticsForm: FormGroup, fields: BaseInput<any>[]) {
    const presentValue = diagnosticsForm.get(AppConstants.patientPresentationLinkId)?.value;

    fields.forEach(field => {
      // The field visibility already calculated by checkForVariations()
      const visible = field.show;
      const datetimeField  = field as DateTimeInput;
      datetimeField.allowUnknown = false;
      datetimeField.allowNotApplicable = false;
      if (!visible) {
        datetimeField.dataMissingReason = '';
      }
      switch (field.linkId) {
        case AppConstants.firstContactParamedicsLinkId:
        case AppConstants.ambulanceEcgLinkId:
          datetimeField.allowUnknown = visible;
          if (!visible) {
            datetimeField.dataMissingReason = '';
          }
          break;
        case AppConstants.registrationDateLinkId:
          if (AppConstants.condition1.includes(presentValue)){
            datetimeField.allowUnknown = visible;
          }
          if (!visible) {
            datetimeField.dataMissingReason = '';
          }
          break;
        case AppConstants.firstHospitalEcgLinkId:
          if (AppConstants.condition4.includes(presentValue)){
            datetimeField.allowNotApplicable = visible;
          }
          if (AppConstants.condition1.includes(presentValue)){
            datetimeField.allowUnknown = visible;
          }
          if (!visible) {
            datetimeField.dataMissingReason = '';
          }
          break;
        case AppConstants.fibrinolysisGivenLinkId:
          // reset errors
          // For first two fields, condition 2 decides field visibility, the last field, condition 3 decides field visibility
          // Unknown visibility is decided by condition 1 all these three fields if the field is visible
          datetimeField.allowUnknown = AppConstants.condition1.includes(presentValue) && visible;
          if (!datetimeField.allowUnknown) {
            datetimeField.dataMissingReason = '';
            // When previously unknown is selected, field.value is set to 'U'
            // then switch patient presentation to make checkbox invisible, also need to reset field value for #3630
            if (diagnosticsForm.get(field.linkId)?.value === 'U') {
              diagnosticsForm.get(field.linkId).patchValue(undefined);
            }
          }
          break;
        // Note: First Balloon do not allow "Unknown" but "Not Applicable"
        case AppConstants.firstBalloonInflationDeviceLinkId:
            datetimeField.allowNotApplicable = visible;
            if (!visible) {
              datetimeField.dataMissingReason = '';
            }
            break;
        default:
          break;
      }
    });
  }

  checkAllOfflistProceduresFilled(offListingForm: FormGroup, fields: BaseInput<any>[]) {
    return !fields.some((data: BaseInput<any>) => data.linkId.includes('procedure-all') && !offListingForm.get(data.linkId).value);
  }

  setDataMissingReasons(formData: BaseInput<any>[], entryDisplayData: IDisplayData[]) {
    entryDisplayData.forEach((data: IDisplayData) => {
      const baseInput = formData.find(item => item.linkId === data.linkId);
      baseInput.dataMissingReason = data.absentReason;
  });
  }
}

export function customDateValidator(conditionString: string, typeOfValidation: string): ValidatorFn {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value) {
      // #3067
      let date = new Date(control.value);
      if (control.value instanceof  Date){
        date = control.value;
      } else if(control.value?.value instanceof Date) {
        date = control.value.value;
      } else if (control.value.hasOwnProperty('value')) {
        return null;
      } else if (!control.value.includes('T')) {
        date = control.value.toDate();
        if (date == null) {
          return null;
        }
      }

      const field = date.getTime();
      const condition = conditionString === 'current' ? new Date().getTime() : conditionString.toDate().getTime();

      switch (typeOfValidation) {
        case 'min':
          if (conditionString === 'current') {
            return field > condition ? { curMin: true } : null;
          }
          return field < condition ? { min: true } : null;
        case 'max':
          if (conditionString === 'current') {
            return field > condition ? { curMax: true } : null;
          }
          return field > condition ? { max: true } : null;
        default:
          break;
      }
    }
    return null;
  };
}

export interface AbstractControlWarn extends AbstractControl { warnings: any; }

export function warningValidator(typeOfField: string, conditionString: string, typeOfValidation: string): ValidatorFn {
  return (control: AbstractControlWarn): { [key: string]: boolean } | null => {
    if (control.value) {
      let field = control.value;
      let condition: number | string = conditionString;
      if (typeOfField === 'number') {
        field = +control.value;
        condition = +conditionString;
      } else if (typeOfField === 'date') {
        field = new Date(control.value).getTime();
        condition = new Date(conditionString).getTime();
        const date = new Date(control.value);
        const inputSplit = control.value.split('-');
        if (control.value.length !== 10 || date.getUTCFullYear() !== +inputSplit[0] || date.getUTCMonth() !== +inputSplit[1] - 1 ||
          date.getUTCDate() !== +inputSplit[2]) {
          return null;
        }
      }

      switch (typeOfValidation) {
        case 'min':
          control.warnings = field < condition ? { warningMin: true } : null;
          break;
        case 'max':
          control.warnings = field > condition ? { warningMax: true } : null;
          break;
        case 'withinMin':
          const curDate = new Date();
          curDate.setDate(curDate.getDate() - +conditionString);
          condition = curDate.getTime();
          control.warnings = field < condition ? { warningWithinMin: true } : null;
          break;
        default:
          break;
      }
    }
    return null;
  };
}


